import { Alert, Box, Icon, LoadingMask, Paragraph, Popover, Tag, Text } from '@zillow/constellation-10';
import { IconArrowDownFilled, IconArrowUpFilled } from '@zillow/constellation-icons';
import { type ComponentProps } from 'react';
import { SalesProCard } from '../../../SharedComponents/SalesProCard';
import {
  type CustomerAnalyticsMarketHighlightsDto,
  MarketAnalyticsWebApiHandler_getMarketHighlights,
  MarketAnalyticsWebApiHandler_getMarketHighlights_Path,
} from '@zg-rentals/ts-customer-analytics-client';
import { useQuery } from '@zg-rentals/react-query5';
import { useMainLayout } from '../../../layouts/MainPageLayout/MainLayoutContext';
import { format, subMonths, subQuarters, subYears } from 'date-fns';

type TagTone = ComponentProps<typeof Tag>['tone'];

type SidebarItem = {
  increaseText: string;
  decreaseText: string;
  key: keyof CustomerAnalyticsMarketHighlightsDto;
};

const sidebarItems: Array<SidebarItem> = [
  {
    increaseText: 'Increase in web searches, month over month.',
    decreaseText: 'Decrease in web searches, month over month.',
    key: 'inventoryQuarterOverQuarter',
  },
  {
    increaseText: 'Increase in multifamily new construction inventory, quarter over quarter.',
    decreaseText: 'Decrease in multifamily new construction inventory, quarter over quarter.',
    key: 'newConstructionInventoryQuarterOverQuarter',
  },
  {
    increaseText: 'Increase in properties offering rent specials, month over month.',
    decreaseText: 'Decrease in properties offering rent specials, month over month.',
    key: 'specialOffersMonthOverMonth',
  },
  {
    increaseText: 'Increase in rent prices, month over month',
    decreaseText: 'Decrease in rent prices, month over month',
    key: 'rentPriceMonthOverMonth',
  },
  {
    increaseText: 'Increase in rent prices, year over year',
    decreaseText: 'Decrease in rent prices, year over year',
    key: 'rentPriceYearOverYear',
  },
];

export const MarketDataReportSidebar = () => {
  const { isFetchingInitialData, selectedMarketId } = useMainLayout();

  const {
    data: marketHighlightsData,
    isLoading: isLoadingMarketHighlights,
    error: marketHighlightsError,
  } = useQuery({
    queryKey: [MarketAnalyticsWebApiHandler_getMarketHighlights_Path, { selectedMarketId }],
    queryFn: () =>
      MarketAnalyticsWebApiHandler_getMarketHighlights({
        zillowRegionId: selectedMarketId,
      }),
  });

  const currentMonthDate = format(new Date(), 'MMM yyyy');
  const previousMonthDate = format(subMonths(new Date(), 1), 'MMM yyyy');
  const currentQuarterDate = format(new Date(), 'QQQ yyyy');
  const previousQuarterDate = format(subQuarters(new Date(), 1), 'QQQ yyyy');
  const currentYearDate = format(new Date(), 'yyyy');
  const previousYearDate = format(subYears(new Date(), 1), 'yyyy');

  return (
    <LoadingMask loading={isLoadingMarketHighlights || isFetchingInitialData} css={{ height: '100%' }}>
      {marketHighlightsError && (
        <Alert tone="critical" css={{ margin: '400' }}>
          Unexpected error loading market highlights
        </Alert>
      )}
      {!marketHighlightsError && (
        <Box
          css={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            height: '100%',
          }}
        >
          <Box
            css={{
              xxl: {
                borderTop: '1px solid',
                borderColor: 'border.muted !important',
              },
            }}
          >
            {sidebarItems
              .filter((item) => marketHighlightsData?.[item.key] && marketHighlightsData[item.key] !== 0)
              .map((item) => {
                const percentage = marketHighlightsData?.[item.key] ?? 0;
                const increase = percentage >= 0;
                let tone = increase ? 'success' : 'critical';
                if (percentage === 0) tone = 'gray';

                const textToShow = increase ? item.increaseText : item.decreaseText;

                const textContent = (
                  <>
                    {textToShow
                      .replace(/month over month\.?/, '')
                      .replace(/quarter over quarter\.?/, '')
                      .replace(/year over year\.?/, '')}{' '}
                    {textToShow.includes('month over month') ? (
                      <Popover
                        trigger={<Text css={{ textDecoration: 'underline' }}>month over month</Text>}
                        header={null}
                        closeButton={<span hidden />}
                        body={<Paragraph>{`${currentMonthDate} vs ${previousMonthDate}`}</Paragraph>}
                      />
                    ) : textToShow.includes('year over year') ? (
                      <Popover
                        trigger={<Text css={{ textDecoration: 'underline' }}>year over year</Text>}
                        header={null}
                        closeButton={<span hidden />}
                        body={<Paragraph>{`${currentYearDate} vs ${previousYearDate}`}</Paragraph>}
                      />
                    ) : (
                      <Popover
                        trigger={<Text css={{ textDecoration: 'underline' }}>quarter over quarter</Text>}
                        header={null}
                        closeButton={<span hidden />}
                        body={<Paragraph>{`${currentQuarterDate} vs ${previousQuarterDate}`}</Paragraph>}
                      />
                    )}
                  </>
                );

                return (
                  <Box
                    key={item.key}
                    css={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '400',
                      padding: '600',
                      borderBottom: '1px solid',
                      borderColor: 'border.muted !important',
                    }}
                  >
                    <Tag tone={tone as TagTone} size="sm">
                      <Box css={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: '100' }}>
                        <Icon>{increase ? <IconArrowUpFilled /> : <IconArrowDownFilled />}</Icon>
                        {Math.abs(percentage).toFixed(1)}%
                      </Box>
                    </Tag>
                    <Paragraph>{textContent}</Paragraph>
                  </Box>
                );
              })}
          </Box>
          <SalesProCard
            title="Meet or beat the market"
            description="Your sales pro can help you navigate market forces and keep your portfolio on track."
            buttonText="Connect with [sales pro name]"
          />
        </Box>
      )}
    </LoadingMask>
  );
};
